<script setup lang="ts">
import { computed } from 'vue';
import type { ProductBundleStep, BundleStepProduct } from '@features/bundle';

const props = defineProps<{
  /** Дефолтный герой-фон бандла (фоллбэк) */
  heroImage: string;
  /** Картинка основного блюда (бургер/ролл) — всегда фиксирована */
  baseProductImg?: string | null;
  /** Все шаги бандла */
  steps?: ProductBundleStep[];
  /** Текущие выборы: step_id → product_id */
  selections?: Record<number, number>;
  /** Placeholder если картинка не загрузилась */
  placeholderImg?: string | null;
}>();

// ─── Helpers ───────────────────────────────────────────────────────────────

const getProductImage = (product: BundleStepProduct): string | null => {
  if (!product.img) return null;
  if (typeof product.img === 'string') return product.img;
  if (Array.isArray(product.img)) return product.img[0]?.url ?? null;
  return null;
};

const getSelectedForStep = (step: ProductBundleStep): BundleStepProduct | null => {
  if (!props.selections) return null;
  const pid = props.selections[step.id];
  if (!pid) return null;
  return step.products.find((p) => p.product_id === pid) ?? null;
};

// ─── Derived images per slot ───────────────────────────────────────────────

const drinkImg = computed<string | null>(() => {
  const step = props.steps?.find((s) => s.step_type === 'drink');
  if (!step) return null;
  const product = getSelectedForStep(step);
  return product ? getProductImage(product) : null;
});

const snackImg = computed<string | null>(() => {
  const step = props.steps?.find((s) => s.step_type === 'snack');
  if (!step) return null;
  const product = getSelectedForStep(step);
  return product ? getProductImage(product) : null;
});

/** Соусы — может быть несколько шагов с type === 'sauce' */
const sauceSteps = computed(() => props.steps?.filter((s) => s.step_type === 'sauce') ?? []);

const sauceImgs = computed<(string | null)[]>(() =>
  sauceSteps.value.map((step) => {
    const product = getSelectedForStep(step);
    return product ? getProductImage(product) : null;
  })
);

const useCompositeScene = computed(() => !!(props.steps && props.steps.length > 0));

const burgerSrc = computed(() => props.baseProductImg || props.heroImage || props.placeholderImg || '');
</script>

<template>
  <div class="bundle-hero">
    <!-- ── Составная сцена ─────────────────────────────────────────── -->
    <div v-if="useCompositeScene" class="bundle-hero__scene">
      <!-- Снэк — правый задний план (z: 2) -->
      <Transition name="bhf" mode="out-in">
        <img
          v-if="snackImg"
          :key="`snack-${snackImg}`"
          :src="snackImg"
          class="bundle-hero__item bundle-hero__snack"
          alt=""
        />
        <div
          v-else-if="steps?.some((s) => s.step_type === 'snack')"
          :key="`snack-ph`"
          class="bundle-hero__item bundle-hero__snack bundle-hero__ph"
        />
      </Transition>

      <!-- Соус 2 (второй шаг соуса) — правый передний план левее (z: 5) -->
      <Transition name="bhf" mode="out-in">
        <img
          v-if="sauceImgs[1]"
          :key="`sauce2-${sauceImgs[1]}`"
          :src="sauceImgs[1]!"
          class="bundle-hero__item bundle-hero__sauce bundle-hero__sauce--2"
          alt=""
        />
        <div
          v-else-if="sauceSteps.length >= 2"
          :key="`sauce2-ph`"
          class="bundle-hero__item bundle-hero__sauce bundle-hero__sauce--2 bundle-hero__ph"
        />
      </Transition>

      <!-- Соус 1 — правый передний план правее (z: 6) -->
      <Transition name="bhf" mode="out-in">
        <img
          v-if="sauceImgs[0]"
          :key="`sauce1-${sauceImgs[0]}`"
          :src="sauceImgs[0]!"
          class="bundle-hero__item bundle-hero__sauce bundle-hero__sauce--1"
          alt=""
        />
        <div
          v-else-if="sauceSteps.length >= 1"
          :key="`sauce1-ph`"
          class="bundle-hero__item bundle-hero__sauce bundle-hero__sauce--1 bundle-hero__ph"
        />
      </Transition>

      <!-- Напиток — центр (z: 4) -->
      <Transition name="bhf" mode="out-in">
        <img
          v-if="drinkImg"
          :key="`drink-${drinkImg}`"
          :src="drinkImg"
          class="bundle-hero__item bundle-hero__drink"
          alt=""
        />
        <div
          v-else-if="steps?.some((s) => s.step_type === 'drink')"
          :key="`drink-ph`"
          class="bundle-hero__item bundle-hero__drink bundle-hero__ph"
        />
      </Transition>

      <!-- Основное блюдо — слева большое (z: 3) -->
      <Transition name="bhf" mode="out-in">
        <img :key="`burger-${burgerSrc}`" :src="burgerSrc" class="bundle-hero__item bundle-hero__burger" alt="" />
      </Transition>
    </div>

    <!-- ── Фоллбэк: одна картинка ─────────────────────────────────── -->
    <Transition v-else name="bhf" mode="out-in">
      <img :key="heroImage" :src="heroImage || placeholderImg || ''" class="bundle-hero__img-full" alt="" />
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.bundle-hero {
  @apply w-full relative overflow-hidden rounded-2xl;
  height: 210px;
  background: transparent;

  @screen md {
    height: 100%;
    border-radius: 0;
  }

  &__scene {
    @apply relative w-full h-full;
  }

  // ── Базовый стиль для всех «слоёв» ───────────────────────────────
  &__item {
    position: absolute;
    bottom: 0;
    object-fit: contain;
    user-select: none;
    pointer-events: none;
  }

  // ── Основное блюдо (бургер/ролл) — слева, крупное ────────────────
  &__burger {
    left: 2%;
    bottom: -13%;
    height: 94%;
    max-width: 56%;
    z-index: 3;
    filter: drop-shadow(4px 6px 10px rgba(0, 0, 0, 0.2));
  }

  // ── Напиток — центр ───────────────────────────────────────────────
  &__drink {
    left: 22%;
    bottom: 17%;
    height: 88%;
    max-width: 64%;
    z-index: 2;
    filter: drop-shadow(2px 4px 8px rgba(0, 0, 0, 0.18));
  }

  // ── Снэк (картошка, наггетсы…) — правый задний план ──────────────
  &__snack {
    right: 5%;
    bottom: 10%;
    height: 68%;
    max-width: 46%;
    z-index: 3;
    filter: drop-shadow(2px 2px 6px rgba(0, 0, 0, 0.14));
  }

  // ── Соусы — правый передний план ─────────────────────────────────
  &__sauce {
    height: 40%;
    max-width: 25%;
    z-index: 5;
    filter: drop-shadow(1px 3px 5px rgba(0, 0, 0, 0.15));

    // первый соус (правее)
    &--1 {
      right: 3%;
      z-index: 6;
    }

    // второй соус (чуть левее)
    &--2 {
      right: 19%;
      z-index: 5;
    }
  }

  // ── Placeholder-заглушка для слота без картинки ───────────────────
  &__ph {
    @apply rounded-xl bg-gray-200/60 dark:bg-gray-700/60;
  }

  // ── Фоллбэк: вся картинка целиком ────────────────────────────────
  &__img-full {
    @apply w-full h-full object-contain;
  }
}

// ── Анимация смены картинок ───────────────────────────────────────────
.bhf-enter-active,
.bhf-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.bhf-enter-from,
.bhf-leave-to {
  opacity: 0;
  transform: scale(0.92) translateY(4px);
}
</style>
